import { Calendar } from "lucide-react";
import { TagInput } from "@/components/TagInput";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSubscriptionTags } from "@/hooks/useSubscriptionTags";
import { type Subscription } from "@/types/subscription";

interface SubscriptionEssentialsFormProps {
  formData: {
    name: string;
    amount: number;
    currency: string;
    frequency: string;
    customFrequencyDays: number;
    nextPayment: string;
    startDate: string;
    status: string;
    billingType: "recurring" | "pay_as_you_go";
  };
  updateFormData: (key: string, value: any) => void;
  tagIds: string[];
  setTagIds: (ids: string[]) => void;
  subscription?: Subscription;
  isEditing: boolean;
  startDateValidation: any;
  setStartDateValidation: (validation: any) => void;
  onStartDateValidationAlert?: React.ReactNode;
}

export const SubscriptionEssentialsForm: React.FC<
  SubscriptionEssentialsFormProps
> = ({
  formData,
  updateFormData,
  tagIds,
  setTagIds,
  subscription,
  isEditing,
  startDateValidation,
  setStartDateValidation,
  onStartDateValidationAlert,
}) => {
  const { replaceTags } = useSubscriptionTags();

  const isPAYG = formData.billingType === "pay_as_you_go";
  const isUnscheduledPAYG =
    isPAYG && (!formData.frequency || formData.frequency === "none");

  const handleTagChange = (ids: string[]) => {
    setTagIds(ids);
    if (isEditing && subscription) {
      replaceTags.mutate({
        subscriptionId: subscription.id,
        categoryIds: ids,
      });
    }
  };

  return (
    <AccordionItem value="essentials">
      <AccordionTrigger>
        <div className="flex items-center gap-2">
          <span>Essentials</span>
          <span className="text-xs text-muted-foreground">(Required)</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-2">
        <div className="space-y-4 pt-2">
          {/* Service Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              Service Name
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => updateFormData("name", e.target.value)}
              placeholder="Netflix, Spotify, etc."
              required
              className="glass-surface"
            />
          </div>

          {/* Billing Type */}
          <div className="space-y-2">
            <Label htmlFor="billingType" className="text-sm font-medium">
              Billing Type
            </Label>
            <Select
              value={formData.billingType}
              onValueChange={(value) => updateFormData("billingType", value)}
            >
              <SelectTrigger className="glass-surface">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recurring">
                  Recurring (fixed amount)
                </SelectItem>
                <SelectItem value="pay_as_you_go">
                  Pay as you go (top-ups)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Choose "Pay as you go" for services without a fixed billing cycle
              (mobile top-ups, prepaid).
            </p>
          </div>

          {/* Amount & Currency */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="amount" className="text-sm font-medium">
                {isPAYG ? "Typical amount (optional)" : "Amount"}
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) =>
                  updateFormData("amount", parseFloat(e.target.value))
                }
                placeholder={isPAYG ? "e.g. 20.00" : "9.99"}
                required={!isPAYG}
                className="glass-surface amount-text"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency" className="text-sm font-medium">
                Currency
              </Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => updateFormData("currency", value)}
              >
                <SelectTrigger className="glass-surface">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Frequency */}
          <div className="space-y-2">
            <Label htmlFor="frequency" className="text-sm font-medium">
              Billing Frequency
            </Label>
            <Select
              value={formData.frequency}
              onValueChange={(value) => {
                updateFormData("frequency", value);
                if (
                  formData.billingType === "pay_as_you_go" &&
                  value === "none"
                ) {
                  updateFormData("nextPayment", "");
                }
              }}
            >
              <SelectTrigger className="glass-surface">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {formData.billingType === "pay_as_you_go" && (
                  <SelectItem value="none">No fixed schedule</SelectItem>
                )}
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="every_2_months">Every 2 months</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Frequency Days */}
          {formData.frequency === "custom" && (
            <div className="space-y-2">
              <Label htmlFor="customDays" className="text-sm font-medium">
                Every X Days
              </Label>
              <Input
                id="customDays"
                type="number"
                min="1"
                value={formData.customFrequencyDays}
                onChange={(e) =>
                  updateFormData(
                    "customFrequencyDays",
                    parseInt(e.target.value),
                  )
                }
                className="glass-surface"
              />
            </div>
          )}

          {/* Next Payment */}
          <div className="space-y-2">
            <Label htmlFor="nextPayment" className="text-sm font-medium">
              Next Payment Date
            </Label>
            <div className="relative">
              <Input
                id="nextPayment"
                type="date"
                value={formData.nextPayment}
                onChange={(e) => updateFormData("nextPayment", e.target.value)}
                required={!isUnscheduledPAYG}
                disabled={isUnscheduledPAYG}
                className="glass-surface"
              />
              <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
            </div>
            {formData.status === "trial" && (
              <p className="text-xs text-muted-foreground">
                For trials, Next payment acts as the trial end date.
              </p>
            )}
          </div>

          {/* Start Date */}
          <div className="space-y-2">
            <Label htmlFor="startDate" className="text-sm font-medium">
              Subscription Start Date
            </Label>
            <div className="relative">
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => {
                  updateFormData("startDate", e.target.value);
                  // Clear previous validation when user changes date
                  if (startDateValidation) {
                    setStartDateValidation(null);
                  }
                }}
                required
                className="glass-surface"
              />
              <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground">
              When did this subscription actually begin?
            </p>

            {/* Show validation results for existing subscriptions */}
            {onStartDateValidationAlert}
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags" className="text-sm font-medium">
              Tags
            </Label>
            <TagInput value={tagIds} onChange={handleTagChange} />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
